// Shared jsPDF drawing primitives, extracted from create-invoice.vue's
// autoGenerateInvoice() so any component generating a similarly-styled
// document (Invoice, Quotation, Credit/Debit Notes) doesn't redefine the
// same ~10 small closures inline. The higher-level section-building logic
// (header band, items table, totals card) stays duplicated-and-modified
// per document type in each component — the sections differ enough
// (badge text, due-date-vs-valid-until, tax breakdown presence) that one
// mega-parameterized function would be a worse maintenance burden than
// the current duplication.

// Palette matching the reference invoice design — shared so every
// generated document has the same visual identity.
export const PDF_COLORS = {
  NAVY: [22, 40, 74], // #16284a
  BLUE: [47, 95, 208], // #2f5fd0
  MUTED: [138, 147, 166], // #8a93a6
  BODY: [82, 92, 110], // #525c6e
  LBODY: [107, 116, 136], // #6b7488
  WHITE: [255, 255, 255],
  DARK: [26, 34, 51], // #1a2233
  BGROW: [247, 249, 252], // #f7f9fc card header bg
  BGALT: [248, 250, 255], // alternating row
  BRDR: [227, 232, 239], // #e3e8ef border
  BLIGHT: [238, 241, 246], // #eef1f6 inner border
  WBLU: [238, 243, 251], // #eef3fb amount-in-words bg
  WBDR: [221, 231, 247], // #dde7f7 amount-in-words border
};

// Returns the set of primitive closures bound to a specific jsPDF `doc`
// instance — call once per document-generation run.
export function makePdfPrimitives(doc) {
  const sc = (...rgb) => doc.setTextColor(...rgb);
  const rc = () => sc(...PDF_COLORS.DARK);

  const fr = (x, y, w, h, rgb) => {
    doc.setFillColor(...rgb);
    doc.setDrawColor(...rgb);
    doc.rect(x, y, w, h, "FD");
  };

  const sr = (x, y, w, h, rgb = PDF_COLORS.BRDR) => {
    doc.setDrawColor(...rgb);
    doc.setLineWidth(0.3);
    doc.rect(x, y, w, h, "S");
  };

  const hl = (x1, x2, yy, rgb = PDF_COLORS.BLIGHT) => {
    doc.setDrawColor(...rgb);
    doc.setLineWidth(0.25);
    doc.line(x1, yy, x2, yy);
  };

  const rl = (lines, x, startY, sz, style, color, lh = 4.5) => {
    doc.setFontSize(sz);
    doc.setFont("helvetica", style);
    if (color) sc(...color);
    else rc();
    lines.forEach((l, i) => doc.text(l, x, startY + i * lh));
    rc();
    return startY + lines.length * lh;
  };

  const wt = (text, maxW) => doc.splitTextToSize(text ? String(text) : "-", maxW);

  const fmtDate = (d) => {
    if (!d) return "-";
    const [yr, mo, dy] = d.split("-");
    return `${dy}/${mo}/${yr}`;
  };

  return { sc, rc, fr, sr, hl, rl, wt, fmtDate };
}
